import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { CostReviewLabelProviderInsurance } from './cost-review-label-provider-insurance/cost-review-label-provider-insurance';
import { CostReviewLineItemDuties } from './cost-review-line-item-duties';
import CostReviewLineItemHeader from './cost-review-line-item-header';
import { CostReviewLineItemShippingService } from './cost-review-line-item-shipping-service/cost-review-line-item-shipping-service';
import { CostReviewLineItemVAT } from './cost-review-line-item-vat';
import style from './cost-review-line-item.module.css';
import { messages } from './messages';
import { useCostReviewLabelProviderLineItems } from './use-cost-review-label-provider-line-items';
import CostReviewLineItemBody from '../../components/cost-review-line-item/cost-review-line-item-body';
import CostReviewLineItemService from '../../components/cost-review-line-item/cost-review-line-item-service';
import CostReviewLineItemSubtotal from '../../components/cost-review-line-item/cost-review-line-item-subtotal';
import { CostReviewLineItemPurchaseContainer } from '../../containers/cost-review/cost-review-line-item-purchase-container';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useDdpCountryConfig } from '../../hooks/use-ddp-country-config';
import { CostReviewHelperService } from '../../services/cost-review-helper-service';
import Global from '../../store/global-selectors';
import { AdditionalChargesCollapsible } from '../modals/label-batch-status/cost-review/additional-charges-collapsible';

/**
 * @typedef {import('../../prop-types/cost-review.types').LabelProviderCostReview} LabelProviderCostReview
 * @typedef {import('../../prop-types/cost-review.types').CostReviewWarning} CostReviewWarning
 * @typedef {import('../../prop-types/cost-review.types').CostReviewFromProviderShippingServiceRecommendationMap} CostReviewFromProviderShippingServiceRecommendationMap
 * @typedef {import('../../prop-types/cost-review.types').CostReviewSelectedRecommendations} CostReviewSelectedRecommendations
 * @typedef {import('../../prop-types/cost-review.types').CostReviewInsuranceRecommendation} CostReviewInsuranceRecommendation
 * @typedef {import('../../prop-types/cost-review.types').CostReviewFromProviderRateShopperRecommendationMap} CostReviewFromProviderRateShopperRecommendationMap
 */

/**
 * @param {object} props
 * @param {LabelProviderCostReview} props.costReviewLineItemProvider
 * @param {(provider: string, amount: number) => void} [props.onPurchaseAmountChange]
 * @param {Array<CostReviewWarning>} [props.costWarnings]
 * @param {CostReviewFromProviderShippingServiceRecommendationMap} [props.shippingServiceRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleShippingServiceRecommendation]
 * @param {boolean} [props.isToggleRecommendationDisabled]
 * @param {CostReviewSelectedRecommendations} [props.selectedShippingServiceRecommendations]
 * @param {(recommendationKey: string) => void} [props.toggleInsuranceRecommendation]
 * @param {CostReviewInsuranceRecommendation} [props.insuranceRecommendation]
 * @param {boolean} [props.isInsuranceRecommendationSelected]
 * @param {CostReviewFromProviderRateShopperRecommendationMap} [props.rateShopperRecommendations]
 * @param {CostReviewSelectedRecommendations} [props.selectedRateShopperRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleRateShopperRecommendation]
 * @returns {React.JSX.Element}
 */
export const CostReviewLabelProviderSection = ({
  costReviewLineItemProvider,
  onPurchaseAmountChange = noop,
  costWarnings = emptyArray,
  shippingServiceRecommendations = emptyObject,
  onToggleShippingServiceRecommendation = noop,
  isToggleRecommendationDisabled = false,
  selectedShippingServiceRecommendations = emptyObject,
  insuranceRecommendation = emptyObject,
  isInsuranceRecommendationSelected = false,
  toggleInsuranceRecommendation = noop,
  rateShopperRecommendations = emptyObject,
  selectedRateShopperRecommendations = emptyObject,
  onToggleRateShopperRecommendation = noop,
}) => {
  const userHasPurchasingRole = useSelector(Global.selectUserhasPurchasingRole);
  const { isDdpAvailable: isPrepaidDutiesAndTaxEnabled } = useDdpCountryConfig();
  const {
    labelProviderId,
    fulfillmentProviderId,
    services,
    balance,
    subTotal,
    sellerLabelProviderId,
    providerInsuranceSubtotal = 0,
    providerConfirmationSubtotal = 0,
    providerOtherCostSubtotal = 0,
  } = costReviewLineItemProvider;
  const lineItems = costReviewLineItemProvider.lineItems || emptyArray;

  const {
    additionalChargesLineItems,
    insuranceLineItems,
    areAdditionalChargesProvided,
    shouldShowConfirmationSubtotal,
    shouldShowOtherCostsSubtotal,
    shouldShowInsurance,
    DutiesLineItem,
  } = useCostReviewLabelProviderLineItems(lineItems, providerInsuranceSubtotal, providerConfirmationSubtotal, providerOtherCostSubtotal);

  const thisProviderServices = (services || []).filter((s) => s.sellerLabelProviderId === sellerLabelProviderId);
  // TODO: use the epxected currency code when it's available
  const providerMaintainsBalance = get(costReviewLineItemProvider, 'providerMaintainsBalance', false);
  const shouldShowPurchase = balance < subTotal && providerMaintainsBalance;
  const { errorMessage, minPurchaseAmount, providerErrorMessage } = CostReviewHelperService.costReviewLineItemProviderMeta(
    costReviewLineItemProvider,
    userHasPurchasingRole,
  );

  return (
    <div className={style.container}>
      <CostReviewLineItemHeader
        balance={balance}
        fulfillmentProviderId={Number.parseInt(fulfillmentProviderId, 10)}
        nickname={get(costReviewLineItemProvider, 'name')}
        providerId={Number.parseInt(labelProviderId, 10)}
        showBalance={providerMaintainsBalance}
      />
      <CostReviewLineItemBody>
        {thisProviderServices.map((service) => (
          <CostReviewLineItemShippingService
            key={service.shippingServiceId}
            isToggleRecommendationDisabled={isToggleRecommendationDisabled}
            onToggleRateShopperRecommendation={onToggleRateShopperRecommendation}
            onToggleRecommendation={onToggleShippingServiceRecommendation}
            rateShopperRecommendations={rateShopperRecommendations[service.shippingServiceId]}
            selectedRateShopperRecommendations={selectedRateShopperRecommendations}
            selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
            service={service}
            shippingServiceRecommendations={shippingServiceRecommendations[service.shippingServiceId]}
          />
        ))}
        {shouldShowInsurance ? (
          <CostReviewLabelProviderInsurance
            insuranceLineItems={insuranceLineItems}
            insuranceRecommendation={insuranceRecommendation}
            insuranceSubtotal={providerInsuranceSubtotal}
            isRecommendationSelected={isInsuranceRecommendationSelected}
            isToggleRecommendationDisabled={isToggleRecommendationDisabled}
            onToggleRecommendation={toggleInsuranceRecommendation}
          />
        ) : null}
        {shouldShowConfirmationSubtotal ? (
          <CostReviewLineItemService
            serviceCost={providerConfirmationSubtotal}
            serviceName={<FormattedMessage {...messages.confirmation} />}
          />
        ) : null}
        {shouldShowOtherCostsSubtotal ? (
          <CostReviewLineItemService
            serviceCost={providerOtherCostSubtotal}
            serviceName={<FormattedMessage {...messages.additionalCharges} />}
          />
        ) : null}
        {areAdditionalChargesProvided ? <AdditionalChargesCollapsible lineItems={additionalChargesLineItems} /> : null}
        <CostReviewLineItemVAT />
        {isPrepaidDutiesAndTaxEnabled ? (
          <CostReviewLineItemDuties currencyCode={DutiesLineItem?.amount.code} isWalleted={false} value={DutiesLineItem?.amount.value} />
        ) : null}
      </CostReviewLineItemBody>
      <CostReviewLineItemSubtotal subtotal={subTotal} />
      {shouldShowPurchase ? (
        <CostReviewLineItemPurchaseContainer
          costWarnings={costWarnings}
          errorMessage={errorMessage}
          labelProviderId={labelProviderId}
          minPurchaseAmount={minPurchaseAmount}
          onPurchaseAmountChange={onPurchaseAmountChange}
          provider={costReviewLineItemProvider}
          providerErrorMessage={providerErrorMessage}
        />
      ) : null}
    </div>
  );
};
